import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  untracked,
} from '@angular/core'

import { FlxButtonComponent } from '@flowx/angular-ui-toolkit'

import { FlxNotificationContentComponent } from '../notification-content/notification-content.component'
import {
  ExtendedNotification,
  NOTIFICATION_DURATION,
  NOTIFICATION_WITH_ACTION_DURATION,
  VISUALS_BY_STATUS,
} from '../notifications.model'

// Used for a grace period so a pause landing just before expiry won't cause the notification to disappear after resuming.
const RESUME_BUFFER = 1000

@Component({
  selector: 'flx-notification',
  imports: [FlxButtonComponent, FlxNotificationContentComponent],
  templateUrl: './notification.component.html',
  host: {
    '[class.enter-animation-disabled]': 'enterAnimationDisabled',
    '(transitionend)': 'disableEnterAnimation($event)',
    // Anywhere on the card counts as having seen it, the close button included.
    '(click)': 'read.emit()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxNotificationComponent {
  #destroyRef = inject(DestroyRef)

  notification = input.required<ExtendedNotification>()
  paused = input.required<boolean>()

  borderClass = computed(() => VISUALS_BY_STATUS[this.notification().status]?.borderClass)

  duration = computed(() =>
    this.notification().action ? NOTIFICATION_WITH_ACTION_DURATION : NOTIFICATION_DURATION
  )

  timerId?: ReturnType<typeof setTimeout>
  remaining = linkedSignal<number>(() => this.duration())
  resumedAt = 0
  enterAnimationDisabled = false

  closed = output<void>()
  read = output<void>()

  constructor() {
    effect(() => {
      const paused = this.paused()
      untracked(() => this.#syncTimer(paused))
    })

    this.#destroyRef.onDestroy(() => clearTimeout(this.timerId))
  }

  // The stack's enter animation hangs off @starting-style, which re-applies on every DOM
  // insertion — and @for re-inserts a surviving card when one above it is dismissed. Once
  // the animation has run, the class opts this card out of the starting style for good.
  // Guarded on the target because transitionend bubbles from the card's own controls.
  disableEnterAnimation(event: TransitionEvent): void {
    if (event.target === event.currentTarget) {
      this.enterAnimationDisabled = true
    }
  }

  #syncTimer(paused: boolean): void {
    if (paused) {
      this.#pause()
    } else {
      this.#start()
    }
  }

  #start(): void {
    if (this.timerId || this.remaining() <= 0) {
      return
    }

    this.resumedAt = Date.now()
    this.timerId = setTimeout(() => this.closed.emit(), this.remaining())
  }

  #pause(): void {
    if (!this.timerId) {
      return
    }

    clearTimeout(this.timerId)
    this.timerId = undefined
    const elapsed = Date.now() - this.resumedAt
    this.remaining.set(Math.max(this.remaining() - elapsed, RESUME_BUFFER))
  }
}
